// Column model for the Accounts table — ported from the mockup `CD`. Shared so the table,
// the column-toggle menu and tests reference one source of truth.

import type { Derived } from '../lib/mappers';
import type { AccountRow } from '../types/api';

export interface ColDef {
  id: string;
  label: string;
  /** default width (px) */
  w: number;
  al: 'left' | 'right';
}

export const COLUMNS: ColDef[] = [
  { id: 'acct', label: 'Account', w: 190, al: 'left' },
  { id: 'conn', label: 'Conn', w: 100, al: 'left' },
  { id: 'bconn', label: 'Broker link', w: 112, al: 'left' },
  { id: 'risk', label: 'Risk', w: 110, al: 'left' },
  { id: 'trade', label: 'Trading', w: 104, al: 'left' },
  { id: 'broker', label: 'Broker / server', w: 148, al: 'left' },
  { id: 'vps', label: 'VPS / term', w: 100, al: 'left' },
  { id: 'strat', label: 'Strategy / tags', w: 138, al: 'left' },
  { id: 'bal', label: 'Balance', w: 112, al: 'right' },
  { id: 'eq', label: 'Equity', w: 112, al: 'right' },
  { id: 'fpl', label: 'Float P&L', w: 106, al: 'right' },
  { id: 'dpl', label: 'Day P&L', w: 106, al: 'right' },
  { id: 'dd', label: 'DD%', w: 76, al: 'right' },
  { id: 'mlvl', label: 'Margin lvl', w: 92, al: 'right' },
  { id: 'fmg', label: 'Free margin', w: 110, al: 'right' },
  { id: 'pos', label: 'Pos', w: 56, al: 'right' },
  { id: 'lots', label: 'Lots', w: 70, al: 'right' },
  { id: 'hb', label: 'Heartbeat', w: 90, al: 'right' },
  { id: 'qt', label: 'Quote', w: 90, al: 'right' },
];

/** Default sort direction per column (1 = asc, -1 = desc), matching the mockup DEF. */
export const SORT_DEFAULT: Record<string, number> = {
  acct: 1,
  conn: 1,
  bconn: 1,
  risk: 1,
  trade: 1,
  broker: 1,
  vps: 1,
  strat: 1,
  bal: -1,
  eq: -1,
  fpl: -1,
  dpl: -1,
  dd: -1,
  mlvl: 1,
  fmg: 1,
  pos: -1,
  lots: -1,
  hb: -1,
  qt: -1,
};

const CONN_ORD: Record<string, number> = { live: 0, delayed: 1, reconnecting: 2, stale: 3, offline: 4 };
const BCONN_ORD: Record<string, number> = { connected: 0, unknown: 1, disconnected: 2 };
const RISK_ORD: Record<string, number> = { normal: 0, warning: 1, critical: 2 };
const TRADE_ORD: Record<string, number> = { open: 0, sync: 1, flat: 2, disabled: 3, inactive: 4 };

/** Sort key accessor for a column. Returns a value comparable with `<`/`>`. */
export function sortAccessor(id: string, a: AccountRow, d: Derived): number | string {
  switch (id) {
    case 'acct':
      return a.label.toLowerCase();
    case 'conn':
      return CONN_ORD[d.conn] ?? 9;
    case 'bconn':
      return BCONN_ORD[d.bc] ?? 9;
    case 'risk':
      return RISK_ORD[d.risk] ?? 9;
    case 'trade':
      return TRADE_ORD[d.tr] ?? 9;
    case 'broker':
      return (a.broker + a.server).toLowerCase();
    case 'vps':
      return a.terminal_id;
    case 'strat':
      return a.strategy.toLowerCase();
    case 'bal':
      return d.balNum ?? -Infinity;
    case 'eq':
      return d.eqNum ?? -Infinity;
    case 'fpl':
      return d.fltNum ?? -Infinity;
    case 'dpl':
      return d.dailyNum ?? -Infinity;
    case 'dd':
      return d.ddNum == null ? -1 : d.ddNum;
    case 'mlvl':
      return d.mlvlNum == null ? Infinity : d.mlvlNum;
    case 'fmg':
      return d.fmgNum == null ? -Infinity : d.fmgNum;
    case 'pos':
      return a.positions_count;
    case 'lots':
      return d.lotsNum ?? 0;
    case 'hb':
      return d.hbAgeMs ?? Infinity;
    case 'qt':
      return d.qtAgeMs ?? Infinity;
    default:
      return a.label.toLowerCase();
  }
}
